import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createColumnHelper, OnChangeFn, SortingState } from '@tanstack/react-table';
import GenericTable from '@/components/GenericTable/GenericTable';
import MultipleTableStatus from '@/components/MultipleTableStatus/MultipleTableStatus';
import { IoEllipsisVerticalSharp } from 'react-icons/io5';
import ActionMenu from '@/pages/friends/components/ActionMenu';
import Button, { ButtonSizes } from '@/components/new-design/Button';
import { replacePathParams } from '@/common/utils/utils';
import ROUTES from '@/routers/Routes';
import useIsMobileView from '@/hooks/useIsMobileView';
import { useNavigate } from 'react-router-dom';
import TableStatusLoader from '@/components/TableStatusLoader/TableStatusLoader';
import AgentTableAvatar from '@/pages/agentDashboard/components/AgentTableAvatar/AgentTableAvatar';
import TetherIcon from '@/features/rewards/Tabs/RedeemTab/components/TetherIcon/TetherIcon';
import ReferralDetailsModal from '@/pages/agentDashboard/components/ReferralDetailsModal/ReferralDetailsModal';
import { AgentRakeShare } from '@/services/rake/rake.types';
import { adaptTableStateToPreview } from '../../utils/utils';

const columnHelper = createColumnHelper<AgentRakeShare>();

const RakeTableDisplay = ({ rake }: { rake: number }) => (
  <div className="flex gap-1">
    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-white">
      {Math.floor(rake * 100) / 100}
    </span>
    <TetherIcon width="20" height="20" />
  </div>
);

interface AgentTableProps {
  data?: AgentRakeShare[];
  isRefetching: boolean;
  isFetching: boolean;
  onClickTransfer: (userId: string) => void;
  onClickShowTables: (userId: string) => void;
  setSorting: (sorting: SortingState) => void;
  sorting: SortingState;
}

const formatDate = (date: string) => {
  const newDate = new Date(date);
  return newDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = (date: string) => {
  const newDate = new Date(date);
  return newDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const AgentTable: FC<AgentTableProps> = ({
  onClickShowTables,
  data,
  isRefetching,
  isFetching,
  onClickTransfer,
  setSorting,
  sorting,
}) => {
  const navigate = useNavigate();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [clickedUserId, setClickedUserId] = useState('');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  const isMobile = useIsMobileView();
  const handleIconClick = (event: React.MouseEvent, userId: string) => {
    event.stopPropagation();
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const yValue = isMobile ? 320 : 440;
    const xValue = isMobile ? 200 : 220;
    setMenuPosition({
      top: rect.top + rect.height - yValue,
      left: rect.left - xValue,
    });
    setActionMenuId(userId);
  };

  const handleCloseMenu = () => {
    setActionMenuId(null);
  };

  const selectReferral = (id: string) => {
    setClickedUserId(id);
    setShowDetailsModal(true);
  };

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor('user.firstName', {
        header: 'Name',
        enableSorting: true,
        minSize: 200,
        cell: ({ row }) => (
          <AgentTableAvatar
            onClick={selectReferral}
            id={row?.original?.user.id}
            email={row?.original.user.email}
            name={`${row?.original?.user.firstName} ${row?.original?.user.lastName}`}
          />
        ),
      }),
      columnHelper.accessor('user.alias', {
        header: 'Alias',
        enableSorting: true,
        minSize: 200,
        cell: ({ row }) => (
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-white">
            {row?.original?.user.alias}
          </span>
        ),
      }),
      columnHelper.accessor('user.createdAt', {
        header: 'Date Joined',
        enableSorting: true,
        minSize: 200,
        //TODO format this time/date
        cell: ({ row }) => (
          <div>
            <div className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-white">
              {formatDate(row?.original?.user.createdAt)}
            </div>
            <div className="overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium text-ph-white-50">
              {formatTime(row?.original?.user.createdAt)}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('dayRake', {
        header: 'Rake Last 24 Hours',

        minSize: 200,
        cell: ({ row }) => <RakeTableDisplay rake={row.original.dayRake} />,
      }),
      columnHelper.accessor('weekRake', {
        header: 'Rake Last 7 Days',

        minSize: 200,
        cell: ({ row }) => <RakeTableDisplay rake={row.original.weekRake} />,
      }),
      columnHelper.accessor('totalRake', {
        header: 'Total Rake',
        enableSorting: true,
        minSize: 200,
        cell: ({ row }) => <RakeTableDisplay rake={row.original.totalRake} />,
      }),
      columnHelper.accessor('tables', {
        header: 'Last Active',

        minSize: 244,
        cell: ({ row }) => {
          const onClickShowDetails = () => onClickShowTables(row.original.user.id);
          const adaptedTables = adaptTableStateToPreview(row.original.tables || []);
          return <MultipleTableStatus tables={adaptedTables} onClickShowDetails={onClickShowDetails} />;
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <div className="flex w-full justify-end px-4">Actions</div>,
        minSize: 140,
        cell: ({ row }) => {
          const isOffline = row.original.tables?.length === 0;
          const moreThanOneTable = row.original.tables?.length && row.original.tables?.length > 1;
          const buttonLabel = moreThanOneTable ? 'Show tables' : 'Join Table';
          const onClickShowTablesInternal = () => {
            if (moreThanOneTable) {
              onClickShowTables(row.original.user.id);
            } else {
              const tableId = row.original.tables && row.original.tables[0].id;
              if (!tableId) {
                return;
              }
              const url = replacePathParams(ROUTES.TABLE, {
                tableId,
              });

              if (isMobile) {
                return navigate(url, { replace: true });
              }
              const width = 1280;
              const height = 780;
              const newWindow = window.open(url, tableId, `height=${height},width=${width}`);
              if (newWindow) {
                newWindow.onload = () => {
                  newWindow.focus();
                };
              }
            }
          };

          return (
            <div className="flex w-full justify-end gap-4">
              <Button size={ButtonSizes.md} onClick={onClickShowTablesInternal} disabled={isOffline} className="w-24">
                {buttonLabel}
              </Button>
              <IoEllipsisVerticalSharp
                onClick={handleActionsMenuClickCreator(row.original.user.id)}
                className="h-10 w-6 cursor-pointer"
              />
            </div>
          );
        },
      }),
    ],
    [],
  );

  const flatData = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!actionMenuRef.current?.contains(event.target as Node)) {
        handleCloseMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const actionMenuRef = useRef<HTMLDivElement>(null);

  const handleActionsMenuClickCreator = (id: string) => (event: React.MouseEvent) => handleIconClick(event, id);

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setClickedUserId('');
  };

  const handleOnClickTransferFounds = useCallback(() => {
    handleCloseMenu();
    if (actionMenuId) onClickTransfer(actionMenuId);
  }, [actionMenuId, onClickTransfer]);

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue([]) : updaterOrValue;
    setSorting(newSorting as SortingState);
  };

  return (
    <div className="scrollbar-phenom relative h-full max-h-[70vh] w-full" ref={tableContainerRef}>
      {flatData.length > 0 && (
        <GenericTable
          onRowClick={undefined}
          data={flatData}
          columns={columns}
          containerRef={tableContainerRef}
          isFetching={isFetching}
          sorting={sorting}
          widthClass="w-full"
          onSortingChange={handleSortingChange}
          gridColClass="grid-cols-[minmax(12em,2fr)_minmax(8em,1fr)_minmax(8.625em,1fr)_minmax(8.625em,1fr)_minmax(8.625em,1fr)_minmax(8.625em,1fr)_minmax(8.625em,1fr)_minmax(8.625em,1fr)]"
        />
      )}
      <TableStatusLoader
        isFetching={!isRefetching && isFetching}
        totalRowCount={flatData?.length || 0}
        noDataLabel="No data to display"
      />
      {actionMenuId && (
        <div
          ref={actionMenuRef}
          className="absolute z-50"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
          }}
        >
          <ActionMenu onClickTransferFounds={handleOnClickTransferFounds} />
        </div>
      )}
      {showDetailsModal && (
        <ReferralDetailsModal setClose={handleCloseModal} open={showDetailsModal} userId={clickedUserId} />
      )}
    </div>
  );
};

export default AgentTable;
